import { useQuery } from "@tanstack/react-query";
import { FEATURE_FLAGS } from "config/featureFlags";
import { useAuthReady } from "hooks/useAuthReady";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { request } from "hooks/useOkoApi/useOkoApi";

/**
 * `GET /feature-flags` returns every active flag already resolved for the
 * signed-in user: `{ flags: { "some_flag": true, ... } }`. The backend owns the
 * precedence (user > active school > client > educator type > default), so the
 * UI only reads the answer and never re-derives it.
 *
 * One query key for the whole map means every consumer shares a single request.
 * The key carries the impersonated identity because the request is answered AS
 * THAT IDENTITY: without it, a map resolved for one teacher would keep being
 * served after the browser starts asking as another.
 */
const flagsQueryKey = (impersonatedUserId) => [
  "feature-flags",
  impersonatedUserId || "self",
];

const EMPTY_FLAGS = {};
const NO_OVERRIDES = [];

export const useRemoteFlags = () => {
  const authReady = useAuthReady();
  const { impersonatedUser } = useImpersonation();

  const { data, isLoading } = useQuery({
    queryKey: flagsQueryKey(impersonatedUser?.id),
    queryFn: () => request("feature-flags", { method: "GET" }),
    // Nothing is asked for until there is a token to ask with.
    enabled: authReady,
    // Flags change when someone edits targeting, not while a teacher works, so
    // a resolved map is kept for the session. A FAILED one is not: caching that
    // forever would leave every gated surface off until a full reload.
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 2,
  });

  return {
    flags: data?.flags || EMPTY_FLAGS,
    superuserOverrides: data?.superuser_overrides || NO_OVERRIDES,
    isLoading: isLoading || !authReady,
  };
};

/**
 * Read one feature flag.
 *
 * Env-var flags resolve synchronously at build time. Remote flags read
 * `defaultValue` until their fetch resolves, so a gated surface stays hidden
 * rather than appearing and then disappearing.
 *
 * @param {string} flagName Key in `FEATURE_FLAGS`.
 * @returns {boolean} Whether the flag is on for this user.
 */
export const useFeatureFlag = (flagName) => {
  const flag = FEATURE_FLAGS[flagName];
  const { flags } = useRemoteFlags();

  if (!flag) return false;

  if (flag.envVar) {
    return process.env[flag.envVar] === "true";
  }

  if (flag.remote) {
    const resolved = flags[flag.key];
    return resolved === undefined ? Boolean(flag.defaultValue) : resolved;
  }

  return Boolean(flag.defaultValue);
};

/**
 * Read one feature flag AND whether its answer has arrived yet.
 *
 * `useFeatureFlag` cannot tell "off" from "not yet known" — it collapses both
 * into the default, which is right for a nav item (better hidden than flashed
 * in) and wrong for a route (a teacher who HAS the flag and pasted a link
 * would be bounced before the answer arrives). A caller that must tell the two
 * apart reads `isLoading` here and holds while it is true.
 *
 * @param {string} flagName Key in `FEATURE_FLAGS`.
 * @returns {{enabled: boolean, isLoading: boolean}}
 */
export const useFeatureFlagStatus = (flagName) => {
  const flag = FEATURE_FLAGS[flagName];
  const { flags, isLoading } = useRemoteFlags();

  if (!flag) return { enabled: false, isLoading: false };

  // An env-var flag is decided at build time, so nothing is ever in flight.
  if (flag.envVar) {
    return { enabled: process.env[flag.envVar] === "true", isLoading: false };
  }

  if (flag.remote) {
    const resolved = flags[flag.key];
    return {
      enabled: resolved === undefined ? Boolean(flag.defaultValue) : resolved,
      isLoading,
    };
  }

  return { enabled: Boolean(flag.defaultValue), isLoading: false };
};
